/**
 * Gruppen der Aufgabenseite (12.10.2026; „Wichtig" ganz oben seit 20.10.2026). Lage, Thema und Grund rechnet der Server
 * (`aufgaben_ordnung.py`) — hier wird nur gruppiert und sortiert, nichts neu entschieden.
 *
 *   Wichtig            von Hand mit der Flagge markiert — über allem anderen (20.10.2026)
 *   Kommende Termine   Aufgaben, deren Projekt (oder eigener Termin) in den nächsten
 *                      7 Tagen dran ist — immer ganz oben, nach Datum
 *   Überfällig         offene Rechnungen über Fälligkeit, lange wartende Helferrechnungen,
 *                      Aufgaben an vergangenen Terminen — das Älteste zuerst
 *   darunter           Themen (Geld · Angebote & Kunden · Planung · Bewertungen · Notizen)
 *                      oder wie früher Kunden
 *
 * Alle Gruppen sind gleich gebaut und lassen sich zuklappen (Benjamin, 12.10.2026).
 */
import { gruppenSchluessel, type AufgabenLage, type AufgabenThema, type Notiz } from "./notizen";

/** Gruppen oben: „Wichtig" (Flagge, 20.10.2026) über den beiden gerechneten Lagen. */
export type GruppenLage = Exclude<AufgabenLage, ""> | "wichtig";
/** `lage` markiert die Gruppen oben — sie sind offen, bis man sie zuklappt. */
export type Gruppe = { schluessel: string; titel: string; notizen: Notiz[]; kunde?: string; lage?: GruppenLage };
export type Ordnungsart = "thema" | "kunde";

export const LAGEN: { id: GruppenLage; titel: string }[] = [
  // Von Hand als wichtig markiert — ganz oben, vor allem Gerechneten (Benjamin, 20.10.2026).
  { id: "wichtig", titel: "Wichtig" },
  { id: "termin", titel: "Kommende Termine" },
  { id: "ueberfaellig", titel: "Überfällig" },
];

export const THEMEN: { id: AufgabenThema; titel: string }[] = [
  { id: "geld", titel: "Geld" },
  { id: "angebote", titel: "Angebote & Kunden" },
  { id: "planung", titel: "Planung" },
  { id: "bewertungen", titel: "Bewertungen" },
  { id: "notizen", titel: "Notizen" },
];

const rang = (n: Notiz) => n.ordnung?.rang ?? 3000;

/** Wichtige zuerst, dann nach Rang (Termin, Verzug, Rest), dann was zuletzt angefasst wurde. */
export function nachRang(a: Notiz, b: Notiz): number {
  return Number(!!b.wichtig) - Number(!!a.wichtig) || rang(a) - rang(b) || (a.updated_at < b.updated_at ? 1 : -1);
}

function gruppe(schluessel: string, titel: string, notizen: Notiz[], extra: Partial<Gruppe> = {}): Gruppe {
  return { schluessel, titel, notizen: [...notizen].sort(nachRang), ...extra };
}

/**
 * `mitLage`: die beiden Gruppen oben bilden (offene Aufgaben, ohne Suche). Beim Suchen
 * steht alles in seinem Thema, damit ein Treffer nicht in zwei Gruppen gesucht werden muss.
 */
export function ordnen(notizen: Notiz[], art: Ordnungsart, mitLage: boolean): Gruppe[] {
  // Eine offene, markierte Aufgabe steht nur unter „Wichtig", nicht zusätzlich in ihrer Lage.
  const lage = (n: Notiz): GruppenLage | "" =>
    (mitLage ? (n.wichtig && n.offen ? "wichtig" : n.ordnung?.lage || "") : "");
  const oben = LAGEN
    .map((l) => gruppe("l:" + l.id, l.titel, notizen.filter((n) => lage(n) === l.id), { lage: l.id }))
    .filter((g) => g.notizen.length > 0);
  const rest = notizen.filter((n) => !lage(n));

  if (art === "thema") {
    return [...oben, ...THEMEN
      .map((t) => gruppe("t:" + t.id, t.titel, rest.filter((n) => (n.ordnung?.thema ?? "notizen") === t.id)))
      .filter((g) => g.notizen.length > 0)];
  }
  const map = new Map<string, { titel: string; notizen: Notiz[] }>();
  for (const n of rest) {
    const g = gruppenSchluessel(n);
    const e = map.get(g.schluessel) || { titel: g.titel, notizen: [] };
    e.notizen.push(n);
    map.set(g.schluessel, e);
  }
  const kunden = [...map.entries()]
    .map(([k, e]) => gruppe(k, e.titel, e.notizen, k.startsWith("k:") ? { kunde: k.slice(2) } : {}))
    // Wer oben in der Reihe steht, steht oben; „Ohne Bezug" immer zuletzt.
    .sort((a, b) => (a.schluessel === "-" ? 1 : b.schluessel === "-" ? -1 : nachRang(a.notizen[0], b.notizen[0])));
  return [...oben, ...kunden];
}
